import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Modal } from '@/components/ui/modal';
import { Badge } from '@/components/ui/badge';
import { SignalIcon } from '@/components/ui/signal-icon';
import { Activity, AlertTriangle, CheckCircle2, Inbox } from 'lucide-react';
import { formatRelativeTime, formatDateTime } from '@/lib/utils';
import { getReadingById } from '../api/dashboard-api';
import type { ActivityFeedItem, MeterReading } from '@/types/api';

const iconMap = {
  reading: <Activity className="w-4 h-4 text-primary-500 flex-shrink-0" />,
  alert_triggered: <AlertTriangle className="w-4 h-4 text-error-500 flex-shrink-0" />,
  alert_resolved: <CheckCircle2 className="w-4 h-4 text-success-500 flex-shrink-0" />,
};

const alarmLabels: [keyof MeterReading, string][] = [
  ['pipeEmpty', 'Pipe Empty'],
  ['noUsage30Days', 'No Usage in 30 Days'],
  ['minTemperatureExceeded', 'Low Temperature'],
  ['maxTemperatureExceeded', 'High Temperature'],
  ['lowSignal', 'Low Signal'],
  ['burstPipeDetected', 'Burst Pipe'],
  ['leakDetected', 'Suspected Leak'],
  ['reverseFlowDetected', 'Reverse Flow'],
  ['lowBattery', 'Low Battery'],
];

export function ActivityFeedCard({ items }: { items: ActivityFeedItem[] }) {
  const navigate = useNavigate();
  const [selectedReading, setSelectedReading] = useState<MeterReading | null>(null);

  async function handleItemClick(item: ActivityFeedItem) {
    if (item.type === 'reading' && item.readingId) {
      const reading = await getReadingById(item.readingId);
      if (reading) {
        setSelectedReading(reading);
        return;
      }
    }
    if (item.deviceId) navigate(`/devices/${item.deviceId}`);
  }

  const activeAlarms = selectedReading
    ? alarmLabels.filter(([key]) => selectedReading[key] === true).map(([, label]) => label)
    : [];

  return (
    <>
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="text-sm">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-1">
              {items.map((item, index) => (
                <button
                  key={`${item.type}-${item.timestamp}-${index}`}
                  onClick={() => handleItemClick(item)}
                  className="w-full flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div className="mt-0.5">
                    {iconMap[item.type]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                      <span className="font-medium">{item.serialNumber}</span>
                      {' '}
                      <span className="text-gray-600 dark:text-gray-400">{item.description}</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {formatRelativeTime(item.timestamp)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={selectedReading !== null}
        onClose={() => setSelectedReading(null)}
        title="Transmission Details"
        description={selectedReading ? formatDateTime(selectedReading.timestamp) : undefined}
        size="md"
      >
        {selectedReading && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Total Usage</p>
                <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">
                  {(selectedReading.computedWaterTotalization ?? selectedReading.waterTotalization).toFixed(2)}{' '}
                  {selectedReading.computedUnitOfMeasure ?? selectedReading.unitOfMeasure}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Temperature</p>
                <p className="text-gray-900 dark:text-gray-100 font-medium">
                  {selectedReading.temperatureF != null ? `${selectedReading.temperatureF.toFixed(1)} °F` : '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Unit</p>
                {selectedReading.unitId ? (
                  <button
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    onClick={() => { setSelectedReading(null); navigate(`/units/${selectedReading.unitId}`); }}
                  >
                    {selectedReading.unitNumber ?? 'Unit'}
                  </button>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100 font-medium">—</p>
                )}
              </div>
            </div>

            {(selectedReading.rssi != null || selectedReading.snr != null) && (
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">Signal</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  {selectedReading.rssi != null && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">RSSI</p>
                      <SignalIcon rssi={selectedReading.rssi} />
                    </div>
                  )}
                  {selectedReading.snr != null && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">SNR</p>
                      <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">{selectedReading.snr} dB</p>
                    </div>
                  )}
                  {selectedReading.spreadingFactor != null && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">SF / BW</p>
                      <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">
                        SF{selectedReading.spreadingFactor}{selectedReading.bandwidth ? ` / ${selectedReading.bandwidth} kHz` : ''}
                      </p>
                    </div>
                  )}
                  {selectedReading.frequency && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">Frequency</p>
                      <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">{selectedReading.frequency}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeAlarms.length > 0 && (
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">Active Alarms</p>
                <div className="space-y-1.5">
                  {activeAlarms.map((label) => (
                    <div key={label} className="flex items-center gap-2 text-sm text-error-700 dark:text-error-500">
                      <span className="w-2 h-2 rounded-full bg-error-500 flex-shrink-0" />
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activeAlarms.length === 0 && (
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Alarms</p>
                <Badge variant="success" size="sm">None</Badge>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
